import React, { useState } from "react";

import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from "@mui/material";
import { Loading, TableSkeletonLoader } from "components";
import { MeasureDataSummaryBatchTable } from "store/types";

interface Props {
  data?: { table: MeasureDataSummaryBatchTable; message: string | null };
  loading: boolean;
}

export const SummaryBatch: React.FC<Props> = ({ data, loading }) => {
  const [isSummaryBatchExpanded, setIsSummaryBatchExpanded] = useState<boolean>(false);

  return (
    <Accordion
      sx={{ boxShadow: "none", "&.Mui-expanded": { m: 0 } }}
      expanded={isSummaryBatchExpanded}
      onChange={() => setIsSummaryBatchExpanded((prevState) => !prevState)}
    >
      <AccordionSummary
        sx={{
          maxHeight: "26px !important",
          minHeight: "26px !important",
          minWidth: "165px",
          position: "absolute",
          top: "-26px",
          bgcolor: "#fff",
          boxShadow: "6px -7px 10px -4px rgba(10, 49, 66, 0.20)",

          p: "5px 20px",
          borderRadius: "0 8px 0 0",
          zIndex: 100,

          ".Mui-expand": {
            maxHeight: "26px !important"
          },
          ".MuiAccordionSummary-content": {
            display: "flex",
            alignItems: "center",
            gap: "5px",
            m: 0
          }
        }}
        aria-controls="panel1-content"
        id="panel1-header"
      >
        <KeyboardArrowUpIcon
          sx={{ fontSize: "16px", ...(isSummaryBatchExpanded && { transform: "rotate(180deg)" }) }}
        />
        <Typography sx={{ fontSize: "12px", fontWeight: 400, color: "#496682", lineHeight: "16px" }}>
          Show Summary
        </Typography>
        {data?.message && <ErrorOutlineIcon sx={{ fill: "red", fontSize: "16px" }} />}
      </AccordionSummary>
      <AccordionDetails sx={{ p: 0 }}>
        <Stack sx={{ p: "20px 20px 0", maxHeight: 300, gap: "20px", minHeight: 100 }}>
          <TableContainer component={Paper} sx={{ boxShadow: "sm", borderRadius: "8px", border: "1px solid #dbe8f5" }}>
            {loading && <TableSkeletonLoader tableProps={{ size: "small" }} rowsNum={4} />}
            {!loading && data && (
              <Table
                stickyHeader
                size="small"
                sx={{
                  // width: "auto",
                  tableLayout: "auto",
                  fontWeight: 400
                }}
              >
                <TableHead>
                  <TableRow>
                    {data?.table.columns.map((col) => (
                      <TableCell
                        sx={{ minWidth: 110, maxWidth: 120, fontSize: "14px", color: "#071F35", fontWeight: 500 }}
                        key={col.key}
                        align="left"
                      >
                        {col.title}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data?.table.rows.map((row, index) => (
                    <TableRow key={index} sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                      {data.table.columns.map((col) => {
                        const cell = row[col.key];
                        return (
                          <TableCell
                            sx={{
                              minWidth: 110,
                              maxWidth: 120,
                              borderBottom: "1px solid #DBE8F5",
                              fontSize: "14px",
                              color: "#071F35",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              ...(cell.color && { color: `${cell.color} !important` }),
                              ":first-child": { fontWeight: 500, color: "#071F35" }
                            }}
                            key={col.key}
                            align="left"
                          >
                            {cell.value}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            {!loading && !data && (
              <Stack sx={{ justifyContent: "center", alignItems: "center", minHeight: 120 }}>
                <Loading loading processTitle="Expecting to have values" />
              </Stack>
            )}
          </TableContainer>
          {data?.message && (
            <Typography
              sx={{ fontSize: "14px", color: "#D93E3E", fontWeight: 400, lineHeight: "16px", whiteSpace: "pre-line" }}
            >
              {data.message}
            </Typography>
          )}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};
